import React, { useState, useEffect, useMemo, useRef } from 'react';
import Layout from '../../components/Layout';
import Card from '../../ui/Card';
import Icon from '../../ui/Icon';
import Button from '../../ui/Button';
import IconButton from '../../ui/IconButton';
import EmptyState from '../../ui/EmptyState';
import { ICONS } from '../../ui/icon-map';
import { Field, Input, Textarea, Checkbox, FormGrid } from '../../ui/Field/Field';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table/Table';
import { CellText, CellPlate, CellMoney, CellMuted } from '../../ui/DataTable/cells';
import { VehicleSearchCard } from './VehicleSelection';
import { fleetService } from '../../services/firestore';
import { onRtaPages } from '../../services/fleetStatus';
import LoadingSpinner from '../../components/LoadingSpinner';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { useModal } from '../../contexts/ModalContext';
import { restoreSavedForm, todayIso, invoiceRowTotal, fitOnPage } from './govForms.logic';
import { vatOf, formatMoney } from '../../constants/finance';
import { useCompanySettings } from '../../services/companySettings';
import InvoiceNumberField, { useInvoiceNumber } from '../../components/InvoiceNumberField';
import { invoiceCounterOn, issueInvoiceNumbers } from '../../services/invoiceNumbers';
import { usePermissions } from '../../hooks/usePermissions';
import logger from '../../utils/logger';

// fleet: { loading, rtaCars } when it is a tab of the Government Transactions page (govFleet.js, the fleet read once
// for all tabs; no page frame of its own); without it a page of its own that reads the fleet itself
const SalesInvoice = ({ fleet, onBusyChange }) => {
    const { showToast } = useModal();
    // permissions phase 4 (4 Oct 2026): the Excel import / template like every government page, the PDF with its
    // invoice number its own
    const { hasPermission } = usePermissions();
    const canGenerate = hasPermission('gov_generate_documents');
    const canInvoice = hasPermission('gov_sales_invoice');
    // our TRN from the company settings (DUP-15: one owner - it was typed here as a fixed value)
    const { settings: companySettings } = useCompanySettings();
    // the invoice number through the one invoice-number service (DUP-23). Today (counter off) it is the Ref
    // Number typed in the form, as before; with the counter on the next free number, refreshed after each invoice.
    const [numberRound, setNumberRound] = useState(0);
    const number = useInvoiceNumber(true, numberRound);
    const [issuedRef, setIssuedRef] = useState('');
    
    // State
    const [ownCars, setAllCars] = useState([]);
    const [ownLoading, setLoading] = useState(!fleet);
    const allCars = fleet ? fleet.rtaCars : ownCars;
    const loading = fleet ? fleet.loading : ownLoading;
    const [generating, setGenerating] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const INITIAL_FORM_STATE = {
        date: todayIso(),
        refNumber: '',
        companyName: 'Al Banoosh Al Muthahida Trad.Co.SPC',
        address1: 'Falaj Alqabail, Sohar',
        address2: 'North Al Batinah Governorate',
        address3: 'Sultanate of Oman',
        footer: 'Saudian Alwefaq Rent A Car Co LLC' // Fixed
    };

    // the saved form comes back, but the date always starts as today (restoreSavedForm)
    const [formData, setFormData] = useState(() => restoreSavedForm(localStorage.getItem('YELO_SALES_INVOICE_DATA'), INITIAL_FORM_STATE));

    const [selectedCars, setSelectedCars] = useState(() => {
        const saved = localStorage.getItem('YELO_SALES_INVOICE_DATA');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                return parsed.selectedCars || [];
            } catch (e) { return []; }
        }
        return [];
    });

    const [bulkPrice, setBulkPrice] = useState('');
    const [includeVat, setIncludeVat] = useState(false);

    const exportTemplateRef = useRef(null);
    const fileInputRef = useRef(null);

    // Initial Load
    useEffect(() => {
        if (!fleet) loadFleet();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // the tabs page locks the other tabs while the PDF is built
    useEffect(() => {
        if (onBusyChange) onBusyChange(generating);
    }, [generating, onBusyChange]);

    // Persist to localStorage
    useEffect(() => {
        const dataToSave = { formData, selectedCars };
        localStorage.setItem('YELO_SALES_INVOICE_DATA', JSON.stringify(dataToSave));
    }, [formData, selectedCars]);

    const loadFleet = async () => {
        setLoading(true);
        try {
            const data = await fleetService.getAllCars(false);
            if (data) {
                setAllCars(data.filter(onRtaPages));   // an RTA page: Ready To Sell stays (still registered); a car RTA dropped goes
            }
        } catch (error) {
            logger.error("Failed to load fleet", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectCar = (car) => {
        // Prevent duplicate plates
        const alreadyAdded = selectedCars.some(c => c.plate_number === car.plate_number);
        if (alreadyAdded) {
            showToast(`${car.plate_number} already added to invoice`, 'warning');
            setSearchQuery('');
            return;
        }
        // Add default amount if not present
        const carWithAmount = {
            ...car,
            sale_amount: car.sale_amount || 0,
            sale_vat: car.sale_vat || 0
        };
        setSelectedCars([...selectedCars, carWithAmount]);
        setSearchQuery('');
    };

    const handleRemoveCar = (index) => {
        const newList = [...selectedCars];
        newList.splice(index, 1);
        setSelectedCars(newList);
    };

    const handleUpdateCar = (index, field, value) => {
        const newList = [...selectedCars];
        newList[index] = { ...newList[index], [field]: value };
        setSelectedCars(newList);
    };

    const handleApplyBulkPrice = () => {
        const price = parseFloat(bulkPrice);
        if (isNaN(price)) {
            showToast('Please enter a valid price', 'warning');
            return;
        }
        const vat = includeVat ? vatOf(price) : 0;
        const newList = selectedCars.map(car => ({
            ...car,
            sale_amount: price,
            sale_vat: vat
        }));
        setSelectedCars(newList);
        showToast(`Applied price ${price} ${includeVat ? 'with VAT' : 'without VAT'} to all ${selectedCars.length} vehicles`, 'success');
    };

    const totalAmount = useMemo(() => {
        return selectedCars.reduce((sum, car) => sum + (parseFloat(car.sale_amount) || 0), 0);
    }, [selectedCars]);

    const totalVat = useMemo(() => {
        return selectedCars.reduce((sum, car) => sum + (parseFloat(car.sale_vat) || 0), 0);
    }, [selectedCars]);

    const grandTotal = totalAmount + totalVat;

    // Number to Words Helper
    const numberToWords = (num) => {
        if (num === 0) return "Zero";
        
        const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
        const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        
        const inWords = (n) => {
            if (n < 20) return a[n];
            if (n < 100) return b[Math.floor(n / 10)] + (n % 10 !== 0 ? '-' + a[n % 10] : ' ');
            if (n < 1000) return a[Math.floor(n / 100)] + 'Hundred ' + (n % 100 !== 0 ? inWords(n % 100) : '');
            if (n < 1000000) return inWords(Math.floor(n / 1000)) + 'Thousand ' + (n % 1000 !== 0 ? inWords(n % 1000) : '');
            if (n < 1000000000) return inWords(Math.floor(n / 1000000)) + 'Million ' + (n % 1000000 !== 0 ? inWords(n % 1000000) : '');
            return '';
        };

        const [integerPart, decimalPart] = num.toFixed(2).split('.');
        let result = inWords(parseInt(integerPart)) + 'Dirhams ';
        
        if (parseInt(decimalPart) > 0) {
            result += 'and ' + inWords(parseInt(decimalPart)) + 'Fils ';
        }
        
        return result.trim() + ' Only';
    };

    const totalInWords = useMemo(() => {
        return numberToWords(grandTotal);
    }, [grandTotal]);

    // Filtered cars for selection
    const filteredCars = useMemo(() => {
        const q = searchQuery.toLowerCase();
        if (!q) return [];
        return allCars.filter(c => 
            (String(c.plate_number).toLowerCase().includes(q) ||
             String(c.make).toLowerCase().includes(q) ||
             String(c.model).toLowerCase().includes(q) ||
             String(c.vin).toLowerCase().includes(q))
        ).slice(0, 10);
    }, [allCars, searchQuery]);

    const handleDownloadTemplate = () => {
        const templateData = selectedCars.length > 0
            ? selectedCars.map(car => ({
                'Chassis No / Plate No': car.vin || car.plate_number || '',
                'Amount':      car.sale_amount || 0,
                'VAT':         car.sale_vat || 0,
            }))
            : [{ 'Chassis No / Plate No': '', 'Amount': 0, 'VAT': 0 }];

        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Vehicles');
        XLSX.writeFile(wb, 'sales_invoice_template.xlsx');
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const data = new Uint8Array(evt.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(sheet);

                if (jsonData.length === 0) {
                    showToast('Excel file is empty or unreadable.', 'error');
                    return;
                }

                // Flexible column finder: matches any key containing the keyword (case-insensitive)
                const findCol = (row, keywords) => {
                    const key = Object.keys(row).find(k =>
                        keywords.some(kw => k.trim().toLowerCase().includes(kw))
                    );
                    return key ? String(row[key]).trim() : null;
                };

                const newSelected = [...selectedCars];
                let addedCount = 0;
                let skippedCount = 0;
                let notFoundCount = 0;

                jsonData.forEach(row => {
                    const plate = findCol(row, ['plate']);
                    const vin   = findCol(row, ['vin', 'chassis']);
                    const amount = parseFloat(findCol(row, ['amount', 'price']) || 0) || 0;
                    const vat    = parseFloat(findCol(row, ['vat', 'tax']) || 0) || 0;

                    const matchedCar = allCars.find(c =>
                        (plate && String(c.plate_number).trim().toLowerCase() === plate.toLowerCase()) ||
                        (vin   && String(c.vin).trim().toLowerCase() === vin.toLowerCase())
                    );

                    if (!matchedCar) { notFoundCount++; return; }

                    // Skip duplicates
                    const alreadyAdded = newSelected.some(c => c.plate_number === matchedCar.plate_number);
                    if (alreadyAdded) { skippedCount++; return; }

                    newSelected.push({ ...matchedCar, sale_amount: amount, sale_vat: vat });
                    addedCount++;
                });

                setSelectedCars(newSelected);

                let msg = `Imported ${addedCount} vehicles.`;
                if (skippedCount > 0) msg += ` ${skippedCount} duplicates skipped.`;
                if (notFoundCount > 0) msg += ` ${notFoundCount} not found in fleet.`;
                showToast(msg, addedCount > 0 ? 'success' : 'warning');

            } catch (error) {
                logger.error("Error reading Excel", error);
                showToast("Failed to read Excel file.", 'error');
            }
        };
        reader.readAsArrayBuffer(file);
        e.target.value = '';
    };

    const handleGeneratePDF = async () => {
        if (selectedCars.length === 0) return;
        let ref;
        try {
            [ref] = invoiceCounterOn()
                ? await number.claim(1)
                : await issueInvoiceNumbers(1, { typed: formData.refNumber, blank: '' });   // ALWFQ-<typed>, as before
        } catch (err) {
            showToast(err?.message || 'The invoice number could not be issued.', 'error');
            return;
        }
        setIssuedRef(ref);
        setGenerating(true);

        try {
            const container = exportTemplateRef.current;
            container.style.display = 'block';

            // Small delay to let React render fully
            await new Promise(r => setTimeout(r, 200));

            const doc = new jsPDF('p', 'mm', 'a4');
            const pdfWidth  = doc.internal.pageSize.getWidth();   // 210mm
            const pdfHeight = doc.internal.pageSize.getHeight();  // 297mm

            // Each page div on its own canvas (as the export letters do). One tall canvas cut into
            // equal slices cut through rows, because the pages are not all the same height
            // (the last one carries the totals) - see fitOnPage.
            const pages = Array.from(container.children);
            for (let i = 0; i < pages.length; i++) {
                const canvas = await html2canvas(pages[i], {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#ffffff',
                });
                const imgData = canvas.toDataURL('image/jpeg', 0.92);
                const at = fitOnPage(canvas.width, canvas.height, pdfWidth, pdfHeight);
                if (i > 0) doc.addPage();
                doc.addImage(imgData, 'JPEG', at.x, at.y, at.width, at.height, undefined, 'FAST');
            }

            doc.save(`Invoice_${invoiceCounterOn() ? ref : (formData.refNumber || 'Draft')}.pdf`);
            container.style.display = 'none';
        } catch (error) {
            logger.error("PDF Generation failed", error);
            showToast("PDF Generation failed", 'error');
        } finally {
            setGenerating(false);
            setNumberRound((n) => n + 1);
        }
    };

    if (loading) return fleet ? <LoadingSpinner /> : <Layout><LoadingSpinner /></Layout>;

    // the one money format (DUP-27); the column is already headed AED
    const money = (n) => formatMoney(n, { currency: false });

    const body = (
        <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">

                {/* Left Column: Form Details */}
                <div className="lg:col-span-1 space-y-6">
                    <Card title="Invoice Details">
                        <div className="space-y-4">
                            <FormGrid cols={2}>
                                <Field label="Date">
                                    <Input
                                        type="date"
                                        value={formData.date}
                                        onChange={(e) => setFormData({...formData, date: e.target.value})}
                                    />
                                </Field>
                                {invoiceCounterOn() ? <InvoiceNumberField state={number} /> : (
                                    <Field label="Ref Number (ALWFQ-)">
                                        <Input
                                            prefix="ALWFQ-"
                                            placeholder="e.g. 26605"
                                            value={formData.refNumber}
                                            onChange={(e) => setFormData({...formData, refNumber: e.target.value})}
                                        />
                                    </Field>
                                )}
                            </FormGrid>

                            <Field label="Buyer Details (To:)" help="Type each line on a new row">
                                <Textarea
                                    rows={5}
                                    placeholder={'Company Name\nAddress Line 1\nAddress Line 2...'}
                                    value={formData.buyerDetails}
                                    onChange={(e) => setFormData({...formData, buyerDetails: e.target.value})}
                                />
                            </Field>

                            <dl className="pt-4 border-t border-gray-100 flex justify-between text-sm">
                                <dt className="text-gray-500">TRN#:</dt>
                                <dd className="font-mono text-gray-900">{companySettings.trn}</dd>
                            </dl>
                        </div>
                    </Card>

                    <VehicleSearchCard
                        title="Add Vehicles"
                        searchQuery={searchQuery}
                        onSearchChange={setSearchQuery}
                        results={filteredCars}
                        onSelect={handleSelectCar}
                        placeholder="Search Plate or VIN..."
                        actionLabel="Add"
                    >
                        {canGenerate && <div className="pt-4 border-t border-gray-100 flex flex-col gap-2">
                            <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" accept=".xlsx,.xls" aria-label="Excel file" />
                            <Button
                                variant="secondary"
                                fullWidth
                                onClick={() => fileInputRef.current.click()}
                                leadingIcon={<Icon glyph={ICONS.upload} size="sm" />}
                            >
                                Import from Excel
                            </Button>
                            <Button
                                variant="ghost"
                                fullWidth
                                onClick={handleDownloadTemplate}
                                leadingIcon={<Icon glyph={ICONS.download} size="sm" />}
                            >
                                Download Template
                            </Button>
                        </div>}
                    </VehicleSearchCard>
                </div>

                {/* Right Column: Vehicle List & Table */}
                <div className="lg:col-span-2 space-y-6">
                    <Card
                        title={`Vehicles (${selectedCars.length})`}
                        actions={
                            <>
                                {selectedCars.length > 0 && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setSelectedCars([])}
                                        leadingIcon={<Icon glyph={ICONS.delete} size="sm" />}
                                    >
                                        Clear All
                                    </Button>
                                )}
                                {canInvoice && <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleGeneratePDF}
                                    disabled={selectedCars.length === 0 || generating}
                                    isLoading={generating}
                                    leadingIcon={<Icon glyph={ICONS.export} size="sm" />}
                                >
                                    {generating ? 'Generating...' : 'Download PDF'}
                                </Button>}
                            </>
                        }
                    >
                        {selectedCars.length > 0 && (
                            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200 flex flex-wrap items-end gap-3">
                                <Field label="Set Universal Price" help="This will set the same price and 5% VAT for every vehicle in the list." className="flex-1 min-w-[200px]">
                                    <Input
                                        type="number"
                                        placeholder="Enter price for all..."
                                        value={bulkPrice}
                                        onChange={(e) => setBulkPrice(e.target.value)}
                                        onWheel={(e) => e.target.blur()}
                                    />
                                </Field>
                                <div className="flex items-center gap-3 pb-6">
                                    <Checkbox
                                        label="Include 5% VAT"
                                        checked={includeVat}
                                        onChange={(e) => setIncludeVat(e.target.checked)}
                                    />
                                    <Button
                                        size="sm"
                                        onClick={handleApplyBulkPrice}
                                        className="whitespace-nowrap"
                                    >
                                        Apply to All
                                    </Button>
                                </div>
                            </div>
                        )}

                        {selectedCars.length > 0 ? (
                            <div>
                                <TableContainer className="overflow-x-auto">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableHeader>Vehicle</TableHeader>
                                                <TableHeader>Plate/VIN</TableHeader>
                                                <TableHeader style={{ width: '120px' }}>Amount</TableHeader>
                                                <TableHeader style={{ width: '100px' }}>VAT (5%)</TableHeader>
                                                <TableHeader>Total</TableHeader>
                                                <TableHeader><span className="sr-only">Actions</span></TableHeader>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {selectedCars.map((car, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>
                                                        <div className="flex flex-col items-center">
                                                            <CellText>{`${car.make} ${car.model}`}</CellText>
                                                            <CellMuted>{car.color} | {car.year}</CellMuted>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex flex-col items-center">
                                                            <CellPlate>{car.plate_number}</CellPlate>
                                                            <CellMuted>{car.vin}</CellMuted>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input
                                                            type="number"
                                                            size="sm"
                                                            aria-label={`Amount for ${car.plate_number}`}
                                                            value={car.sale_amount}
                                                            onChange={(e) => handleUpdateCar(idx, 'sale_amount', e.target.value)}
                                                            onWheel={(e) => e.target.blur()}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input
                                                            type="number"
                                                            size="sm"
                                                            aria-label={`VAT for ${car.plate_number}`}
                                                            value={car.sale_vat}
                                                            onChange={(e) => handleUpdateCar(idx, 'sale_vat', e.target.value)}
                                                            onWheel={(e) => e.target.blur()}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <CellMoney value={invoiceRowTotal(car)} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <IconButton
                                                            icon={ICONS.delete}
                                                            label={`Remove ${car.plate_number}`}
                                                            size="sm"
                                                            onClick={() => handleRemoveCar(idx)}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow className="is-total">
                                                <TableCell colSpan={2}>Totals:</TableCell>
                                                <TableCell><span className="ds-cell-num">{money(totalAmount)}</span></TableCell>
                                                <TableCell><span className="ds-cell-num">{money(totalVat)}</span></TableCell>
                                                <TableCell><span className="ds-cell-num">{money(grandTotal)}</span></TableCell>
                                                <TableCell />
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <div className="text-xs font-medium text-gray-500 mb-1">Total in Words</div>
                                    <div className="font-semibold text-gray-900">
                                        {totalInWords}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <EmptyState
                                icon={ICONS.fleet}
                                title="No vehicles added to the invoice"
                                description="Search for a vehicle or import an Excel file."
                            />
                        )}
                    </Card>
                </div>
            </div>

            {/* Hidden Template for PDF Rendering */}
            <div 
                ref={exportTemplateRef} 
                className="pdf-render-container"
                style={{
                    position: 'absolute',
                    left: '-9999px',
                    top: '0',
                    width: '210mm',
                    background: 'white',
                    display: 'none',
                    color: 'black'
                }}
            >
                {(() => {
                    const CARS_PER_PAGE = 14;
                    const pages = [];
                    for (let i = 0; i < selectedCars.length; i += CARS_PER_PAGE) {
                        pages.push(selectedCars.slice(i, i + CARS_PER_PAGE));
                    }
                    if (pages.length === 0) pages.push([]);
                    const totalPages = pages.length;
                    const formattedDate = new Date(formData.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

                    const tableHeader = (
                        <thead>
                            <tr style={{ background: '#f9f9f9', height: '10mm', whiteSpace: 'nowrap' }}>
                                <th style={{ border: '1pt solid black', width: '35px', padding: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm', padding: '0 3mm' }}>S No</div>
                                </th>
                                <th style={{ border: '1pt solid black', width: '95px', padding: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm', padding: '0 3mm' }}>Model</div>
                                </th>
                                <th style={{ border: '1pt solid black', width: '45px', padding: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm' }}>Year</div>
                                </th>
                                <th style={{ border: '1pt solid black', width: '85px', padding: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm' }}>Plate No</div>
                                </th>
                                <th style={{ border: '1pt solid black', width: '65px', padding: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm' }}>Color</div>
                                </th>
                                <th style={{ border: '1pt solid black', padding: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm' }}>Chassis No</div>
                                </th>
                                <th style={{ border: '1pt solid black', width: '90px', padding: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm' }}>Amount</div>
                                </th>
                                <th style={{ border: '1pt solid black', width: '80px', padding: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm' }}>VAT 5%</div>
                                </th>
                                <th style={{ border: '1pt solid black', width: '90px', padding: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm' }}>Total</div>
                                </th>
                            </tr>
                        </thead>
                    );

                    return pages.map((pageRows, pageIdx) => {
                        const isLastPage = pageIdx === totalPages - 1;
                        const startIdx = pageIdx * CARS_PER_PAGE;

                        return (
                            <div key={pageIdx} style={{
                                width: '210mm',
                                minHeight: '297mm',
                                padding: '30mm 12mm 55mm 12mm',
                                fontFamily: 'Arial, sans-serif',
                                fontSize: '11pt',
                                boxSizing: 'border-box',
                                position: 'relative',
                                pageBreakAfter: isLastPage ? 'auto' : 'always'
                            }}>
                                {/* Header - appears on every page */}
                                <div style={{ marginBottom: '5mm' }}>
                                    <h1 style={{ fontSize: '24pt', fontWeight: 'bold', marginBottom: '3mm' }}>Tax Invoice</h1>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontSize: '10pt' }}>
                                        <div>{formattedDate}</div>
                                        <div style={{ textAlign: 'right' }}>
                                            <table style={{ marginLeft: 'auto', borderCollapse: 'collapse', fontSize: '10pt' }}>
                                                <tbody>
                                                    <tr>
                                                        <td style={{ textAlign: 'left', paddingRight: '1mm' }}>Ref</td>
                                                        <td style={{ textAlign: 'center', paddingRight: '1mm' }}>:</td>
                                                        <td style={{ textAlign: 'left' }}>{issuedRef}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ textAlign: 'left', paddingRight: '1mm' }}>TRN#</td>
                                                        <td style={{ textAlign: 'center', paddingRight: '1mm' }}>:</td>
                                                        <td style={{ textAlign: 'left' }}>{companySettings.trn}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                {/* Buyer Info - appears on every page */}
                                <div style={{ marginBottom: '8mm' }}>
                                    <div style={{ whiteSpace: 'pre-wrap', fontSize: '10pt' }}>
                                        {formData.buyerDetails}
                                    </div>
                                </div>

                                {/* Table */}
                                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '5mm', fontSize: '9pt', border: '1pt solid black' }}>
                                    {tableHeader}
                                    <tbody>
                                        {pageRows.map((car, rowIdx) => {
                                            const globalIdx = startIdx + rowIdx;
                                            const amount = parseFloat(car.sale_amount) || 0;
                                            const vat = parseFloat(car.sale_vat) || 0;
                                            const total = amount + vat;
                                            return (
                                                <tr key={globalIdx} style={{ height: '10mm', whiteSpace: 'nowrap' }}>
                                                    <td style={{ border: '1pt solid black', padding: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm', padding: '0 3mm' }}>{globalIdx + 1}</div>
                                                    </td>
                                                    <td style={{ border: '1pt solid black', padding: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm', padding: '0 3mm' }}>{car.make} {car.model}</div>
                                                    </td>
                                                    <td style={{ border: '1pt solid black', padding: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm', fontSize: '8pt' }}>{car.year || '-'}</div>
                                                    </td>
                                                    <td style={{ border: '1pt solid black', padding: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm' }}>{car.plate_number}</div>
                                                    </td>
                                                    <td style={{ border: '1pt solid black', padding: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '10mm', padding: '1mm 2mm', textAlign: 'center', whiteSpace: 'normal' }}>{car.color}</div>
                                                    </td>
                                                    <td style={{ border: '1pt solid black', padding: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm', fontSize: '7.5pt', padding: '0 2mm' }}>{car.vin}</div>
                                                    </td>
                                                    <td style={{ border: '1pt solid black', padding: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm' }}>{money(amount)}</div>
                                                    </td>
                                                    <td style={{ border: '1pt solid black', padding: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm' }}>{vat > 0 ? money(vat) : '-'}</div>
                                                    </td>
                                                    <td style={{ border: '1pt solid black', padding: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm', fontWeight: 'bold' }}>{money(total)}</div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    {/* Grand Total row only on last page */}
                                    {isLastPage && (
                                        <tfoot>
                                            <tr style={{ fontWeight: 'bold', height: '10mm', whiteSpace: 'nowrap' }}>
                                                <td colSpan={6} style={{ border: '1pt solid black', padding: 0 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm' }}>Total:</div>
                                                </td>
                                                <td style={{ border: '1pt solid black', padding: 0 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm' }}>{money(totalAmount)}</div>
                                                </td>
                                                <td style={{ border: '1pt solid black', padding: 0 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm' }}>{totalVat > 0 ? money(totalVat) : '-'}</div>
                                                </td>
                                                <td style={{ border: '1pt solid black', padding: 0 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10mm' }}>{money(grandTotal)}</div>
                                                </td>
                                            </tr>
                                        </tfoot>
                                    )}
                                </table>

                                {/* Total in Words & Footer - last page only */}
                                {isLastPage && (
                                    <>
                                        <div style={{ marginBottom: '10mm', fontWeight: 'bold' }}>
                                            Total In Words : {totalInWords}
                                        </div>
                                        <div style={{ marginTop: '10mm' }}>
                                            <div>Best Regards,</div>
                                            <div style={{ fontWeight: 'bold', marginTop: '3mm' }}>{formData.footer}</div>
                                        </div>
                                    </>
                                )}

                                {/* Page Number */}
                                <div style={{ position: 'absolute', bottom: '30mm', left: 0, right: 0, textAlign: 'center', fontSize: '10pt' }}>
                                    Page <span style={{ fontWeight: 'bold' }}>{pageIdx + 1}</span> of <span style={{ fontWeight: 'bold' }}>{totalPages}</span>
                                </div>
                            </div>
                        );
                    });
                })()}
            </div>
        </>
    );

    if (fleet) return body;
    return (
        <Layout
            title="Sales Invoice"
            subtitle="Government Transactions"
            showHome={true}
            backPath="/government"
        >
            {body}
        </Layout>
    );
};

export default SalesInvoice;
